import { defaultSessionPolicy, type SessionPolicy } from './session-policy.js';
import { sectionPermissions, type SectionPermission } from './sections.js';

/**
 * Permission bundles are additive: a person's permissions are the union of every active assignment,
 * and no bundle implies another. In particular `club_admin` does not include finance
 * capabilities; those are assigned separately (specification section 4).
 */
export const permissionBundles = [
  'club_admin',
  'secretary',
  'finance',
  'coach',
  'registrar',
  'rules_officer',
  'guardian',
  'athlete',
] as const;
export type PermissionBundle = (typeof permissionBundles)[number];

/**
 * Bundles that were switched off. Existing rows keep their name for history and display but grant
 * nothing, and they cannot be assigned or invited. The registrar's duties moved to `club_admin`,
 * `secretary` and `coach`; the rules officer's moved to `club_admin`.
 */
export const retiredBundles: readonly PermissionBundle[] = ['registrar', 'rules_officer'];

export function isRetiredBundle(bundle: PermissionBundle): boolean {
  return retiredBundles.includes(bundle);
}

export function isPermissionBundle(candidate: unknown): candidate is PermissionBundle {
  return permissionBundles.some((bundle) => bundle === candidate);
}

/** Action permissions beyond the section view permissions used for navigation. */
export const actionPermissions = [
  'roles.manage',
  /** Read and change the club's configuration, such as its session policy. */
  'organization.configure',
  'invitations.manage',
  'guardians.verify',
  /** See every athlete in scope (the whole club or the held departments), not only linked children. */
  'athletes.browse',
  /** Create and correct athlete records, including contact details. Organization-wide only. */
  'athletes.manage',
  /** See the athletes the person is a guardian of through a current approved link. */
  'children.view',
  /** Create seasons, teams and training groups and place athletes on them, per department. */
  'teams.manage',
  /** Read published events of the club or the held departments, not only the events of linked children. */
  'events.browse',
  /** Create, change, publish and cancel events and event series; also sees drafts. Per department. */
  'events.manage',
  /** Create venues and bookable resources such as rink zones. */
  'resources.manage',
  /** Record attendance and correct an answer after the deadline. Per department. */
  'attendance.record',
  /** See and answer the events of the athletes a guardian is linked to, or of one's own athlete record. */
  'schedule.own',
  /** Prepare drills and training plans. Per department. */
  'training.manage',
  /** Define assessment protocols, run sessions and record, correct and publish results. Per department. */
  'assessments.manage',
  /** See the published results of the athletes a guardian is linked to, or of one's own athlete record. */
  'results.own',
  /** Stage, resolve and commit people imports. Organization-wide only. */
  'imports.manage',
  /** Post charges, payments, allocations, credits, refunds and reversals and approve account access. Organization-wide only. */
  'billing.manage',
  /** Create competition seasons and (later) registrations and rosters. Per department. */
  'competitions.manage',
  /** Verify the sources of rule profiles and activate or supersede them. Organization-wide only. */
  'rules.manage',
] as const;
export type ActionPermission = (typeof actionPermissions)[number];

export type Permission = SectionPermission | ActionPermission;

const bundlePermissions: Readonly<Record<PermissionBundle, readonly Permission[]>> = {
  club_admin: [
    'overview.view',
    'athletes.view',
    'teams.view',
    'calendar.view',
    'reports.view',
    'settings.view',
    'competitions.view',
    'roles.manage',
    'organization.configure',
    'invitations.manage',
    'athletes.browse',
    'athletes.manage',
    'imports.manage',
    'teams.manage',
    'events.browse',
    'events.manage',
    'resources.manage',
    'competitions.manage',
    'rules.manage',
  ],
  secretary: [
    'overview.view',
    'athletes.view',
    'teams.view',
    'calendar.view',
    'reports.view',
    'competitions.view',
    'invitations.manage',
    'guardians.verify',
    'athletes.browse',
    'athletes.manage',
    'imports.manage',
    'teams.manage',
    'events.browse',
    'events.manage',
    'resources.manage',
    'competitions.manage',
  ],
  finance: ['overview.view', 'billing.view', 'billing.manage', 'reports.view'],
  coach: [
    'overview.view',
    'athletes.view',
    'teams.view',
    'calendar.view',
    'training.view',
    'assessments.view',
    'competitions.view',
    'athletes.browse',
    'events.browse',
    'events.manage',
    'attendance.record',
    'training.manage',
    'assessments.manage',
    'competitions.manage',
  ],
  /** Retired: grants nothing (see `retiredBundles`). */
  registrar: [],
  /** Retired: grants nothing (see `retiredBundles`). */
  rules_officer: [],
  guardian: [
    'overview.view',
    'athletes.view',
    'calendar.view',
    'assessments.view',
    'children.view',
    'schedule.own',
    'results.own',
    'payments.view',
  ],
  athlete: ['overview.view', 'calendar.view', 'assessments.view', 'schedule.own', 'results.own'],
};

/** Bundles for people who work for the club; guardians and athletes use the family experience. */
export const staffBundles: readonly PermissionBundle[] = [
  'club_admin',
  'secretary',
  'finance',
  'coach',
  'registrar',
  'rules_officer',
];

/** Operational roles a club-wide administrator may explicitly add to their own membership. */
export const selfAssignableBundles: readonly PermissionBundle[] = ['secretary', 'finance', 'coach'];

/** Bundles that act on behalf of, or as, a person and therefore need an approved person link. */
export const personLinkedBundles: readonly PermissionBundle[] = ['guardian', 'athlete'];

export function isStaffBundle(bundle: PermissionBundle): boolean {
  return staffBundles.includes(bundle);
}

export function requiresPersonLink(bundle: PermissionBundle): boolean {
  return personLinkedBundles.includes(bundle);
}

export function permissionsOfBundle(bundle: PermissionBundle): readonly Permission[] {
  return bundlePermissions[bundle];
}

/** A role assignment as stored; `departmentId` null means the whole organization. */
export interface RoleAssignmentRecord {
  permissionBundle: string;
  departmentId: string | null;
  validFrom: Date;
  validUntil: Date | null;
  revokedAt: Date | null;
}

export interface PermissionGrant {
  permission: Permission;
  /** Null grants the permission for the whole organization. */
  departmentId: string | null;
}

/** True when any active assignment is a staff bundle; staff access is what MFA protects. */
export function holdsStaffBundle(assignments: readonly RoleAssignmentRecord[], now: Date): boolean {
  return assignments.some(
    (assignment) =>
      isAssignmentActive(assignment, now) &&
      isPermissionBundle(assignment.permissionBundle) &&
      isStaffBundle(assignment.permissionBundle),
  );
}

export function isAssignmentActive(assignment: RoleAssignmentRecord, now: Date): boolean {
  if (assignment.revokedAt !== null) return false;
  if (assignment.validFrom.getTime() > now.getTime()) return false;
  return assignment.validUntil === null || assignment.validUntil.getTime() > now.getTime();
}

/**
 * Expands the active assignments into grants. A bundle name this release does not know grants
 * nothing, so a stale or tampered row can never widen access.
 */
export function resolveGrants(
  assignments: readonly RoleAssignmentRecord[],
  now: Date,
): PermissionGrant[] {
  const unique = new Map<string, PermissionGrant>();
  for (const assignment of assignments) {
    if (!isAssignmentActive(assignment, now)) continue;
    if (!isPermissionBundle(assignment.permissionBundle)) continue;
    if (isRetiredBundle(assignment.permissionBundle)) continue;
    for (const permission of permissionsOfBundle(assignment.permissionBundle)) {
      unique.set(`${permission}|${assignment.departmentId ?? ''}`, {
        permission,
        departmentId: assignment.departmentId,
      });
    }
  }
  return [...unique.values()];
}

/**
 * True when a grant covers the permission. Without `departmentId` only an organization-wide grant
 * qualifies; with it, an organization-wide grant or one for exactly that department does.
 */
export function isPermitted(
  grants: readonly PermissionGrant[],
  permission: Permission,
  departmentId: string | null = null,
): boolean {
  return grants.some(
    (grant) =>
      grant.permission === permission &&
      (grant.departmentId === null || grant.departmentId === departmentId),
  );
}

/** True when the permission is held in any scope; use it to gate a route, then narrow by record scope. */
export function holdsInAnyScope(
  grants: readonly PermissionGrant[],
  permission: Permission,
): boolean {
  return grants.some((grant) => grant.permission === permission);
}

/** Departments in which the permission is held. Null means it is held organization-wide. */
export function departmentsWithPermission(
  grants: readonly PermissionGrant[],
  permission: Permission,
): string[] | null {
  const matching = grants.filter((grant) => grant.permission === permission);
  if (matching.some((grant) => grant.departmentId === null)) return null;
  return matching.flatMap((grant) => (grant.departmentId === null ? [] : [grant.departmentId]));
}

/** Sections the person may open in at least one scope; record scope is enforced by the API. */
export function permittedSections(grants: readonly PermissionGrant[]): SectionPermission[] {
  return sectionPermissions.filter((section) =>
    grants.some((grant) => grant.permission === section),
  );
}

/**
 * Bundles a caller may offer in an invitation. Staff bundles need `roles.manage`; a secretary-level
 * `invitations.manage` is limited to guardians and athletes, which also need a verified person link.
 */
export function invitableBundles(
  grants: readonly PermissionGrant[],
  departmentId: string | null,
): PermissionBundle[] {
  if (isPermitted(grants, 'roles.manage', departmentId)) {
    return permissionBundles.filter((bundle) => !isRetiredBundle(bundle));
  }
  if (isPermitted(grants, 'invitations.manage', departmentId)) return [...personLinkedBundles];
  return [];
}

/** Idle timeout is the strictest of the person's roles: staff 30 minutes, family 12 hours. */
export const staffIdleTimeoutSeconds = 30 * 60;
export const familyIdleTimeoutSeconds = 12 * 60 * 60;

export function idleTimeoutFor(
  assignments: readonly RoleAssignmentRecord[],
  now: Date,
  policy: Pick<SessionPolicy, 'staffIdleMinutes' | 'familyIdleMinutes'> = defaultSessionPolicy,
): number {
  const activeBundles = assignments
    .filter((assignment) => isAssignmentActive(assignment, now))
    .map((assignment) => assignment.permissionBundle)
    .filter(isPermissionBundle);
  // No known role is treated like staff: the stricter timeout is the safe default.
  const familyOnly = activeBundles.length > 0 && !activeBundles.some(isStaffBundle);
  return (familyOnly ? policy.familyIdleMinutes : policy.staffIdleMinutes) * 60;
}
